import { Box, IconButton, Typography, useTheme, type Theme } from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useMemo, useState, useRef, useEffect, useCallback, Fragment } from "react";
import { getSpanColor } from "../utils/colors";
import { clamp } from "../utils/math";

type CompLaneSpan = { 
  id: string;
  span: ISpan, 
  depth: number, 
  color: string, 
  parents: Set<string>;
};

interface CompLaneProps {
  span: CompLaneSpan;
  bounds: { min: number; max: number; duration: number };
  spanTreeMode: { ids: Set<string>, mode: 'path' | 'only' };
  theme: Theme;
  onClick: (span: ISpan) => void;
  onHover: (span: ISpan, active: boolean) => void;
  onToggle: (span: ISpan, active: boolean) => void;
}

function CompLane({ span, bounds, spanTreeMode, theme, onHover, onClick, onToggle }: CompLaneProps) {
  const [isHighlighted, setIsHighlighted] = useState(false);

  return (
    <div
      style={{
        cursor: "pointer",
        display: "contents",
        ["--bg" as any]: isHighlighted ? `${theme.palette.action.selected}` : "transparent",
      }}
      onMouseEnter={() => {
        onHover(span.span, true);
        setIsHighlighted(true);
      }}
      onMouseLeave={() => {
        onHover(span.span, false);
        setIsHighlighted(false);
      }}
    >
      <div
        style={{
          gridColumn: 1,

          padding: `${theme.spacing(0.2, 1)}`,

          backgroundColor: `var(--bg)`,
        }}
      >
        <div
          style={{
            marginLeft: `${span.depth * 8}px`,
            marginRight: 0,

            padding: `${theme.spacing(0, 1)}`,

            borderRight: `1px solid ${theme.palette.divider}`,

            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.4),
          }}
        >
          {spanTreeMode.mode == 'path' && span.span.children && span.span.children.length > 0 ? 
          (
             spanTreeMode.ids.has(span.span.id) ? (
              <ExpandMoreIcon sx={{ fontSize: 16 }} onClick={(e) => { e.stopPropagation(); onToggle(span.span, false); }} />
            ) : (
              <ExpandLessIcon sx={{ fontSize: 16 }} onClick={(e) => { e.stopPropagation(); onToggle(span.span, true); }} />
            )
          ) : (
            <Box sx={{ width: 16 }} />
          )}
          
          <Typography fontFamily="monospace" fontSize={12} variant="caption" textOverflow={'ellipsis'} overflow={'hidden'} flex={1} onClick={() => onClick(span.span)}>{span.span.name}</Typography>
          <span>{span.span.duration}ms</span>
        </div>
      </div>
      <div
        style={{
          gridColumn: 2,

          backgroundColor: `var(--bg)`,

          padding: `${theme.spacing(0.2, 0)}`,
        }}
        onClick={() => onClick(span.span)}
      >
        {span.span.duration > 20 ? (
        <div
          style={{
            color: `${theme.palette.text.primary}`,

            padding: `${theme.spacing(0, 1)}`,

            width: `${(span.span.duration / bounds.duration) * 100}%`,
            marginLeft: `${((span.span.start.getTime() - bounds.min) / bounds.duration) * 100}%`,

            // borderRadius: `4px`,
            backgroundColor: span.color,
          }}
        >
            {span.span.duration}ms
        </div>
        ) : (
          <div
            style={{
              color: `${theme.palette.text.primary}`,

              width: `${(span.span.duration / bounds.duration) * 100}%`,
              marginLeft: `${((span.span.start.getTime() - bounds.min) / bounds.duration) * 100}%`,

              backgroundColor: span.color,
            }}
          >
            &nbsp;
          </div>
        )}
      </div>
    </div>
  );
}

export interface CompLanesProps {
  spans: ISpan[];
  autoExpand?: number;
  filterSpanIds?: string[] | null;
  onClick: (span: ISpan) => void;
  onHover: (span: ISpan, active: boolean) => void;
}

interface ISpanTreeMode {
  ids: Set<string>;
  mode: 'path' | 'only';
  isExpanded: (span: CompLaneSpan, ids: Set<string>) => boolean;
}

const fnExpandedContains = (span: CompLaneSpan, ids: Set<string>) => {
  return ids.has(span.id);
}

const fnExpandedIsSupersetOf = (span: CompLaneSpan, ids: Set<string>) => {
  return ids.isSupersetOf(span.parents);
}

export function CompLanes({ spans, autoExpand, filterSpanIds, onHover, onClick }: CompLanesProps) {
  const theme = useTheme();
  const draggingRef = useRef<{ startX: number, startColWidth: number } | null>(null);
  const [colWidth, setColWidth] = useState(400);
  const [spanTreeMode, setSpanTreeMode] = useState<ISpanTreeMode>({ 
    ids: new Set<string>(), 
    mode: 'path', 
    isExpanded: fnExpandedIsSupersetOf
  });

  const { spanList, spanBounds, steppers } = useMemo(() => flattenTree(spans), [spans]);

  autoExpand ??= -1;

  useEffect(() => {
    const ids = spanTreeMode.ids;

    ids.clear();

    if (filterSpanIds) {
      for (const x of spanList) {
        if (filterSpanIds.includes(x.id)) {
          ids.add(x.id);

          for (const parent of x.parents) {
            ids.add(parent);
          }
        }
      }

      setSpanTreeMode({ 
        ids: ids, 
        mode: 'only', 
        isExpanded: fnExpandedContains
      });
      return;
    }

    if (autoExpand == 0) {
      setSpanTreeMode({ 
        ids: ids, 
        mode: 'path', 
        isExpanded: fnExpandedIsSupersetOf
      });
      return;
    }

    for (const x of spanList) {
      if (x.depth < autoExpand || autoExpand == -1) {
        ids.add(x.span.id);
      }
    }

    setSpanTreeMode({ 
      ids: ids, 
      mode: 'path',
      isExpanded: fnExpandedIsSupersetOf
    });
  }, [spanList, autoExpand, filterSpanIds]);

  const onToggle = (span: ISpan, active: boolean) => {
    if (spanTreeMode.mode == 'only') {
      return;
    }

    const ids = spanTreeMode.ids;

    if (active) {
      ids.add(span.id);
    } else {
      ids.delete(span.id);
    }

    setSpanTreeMode({ 
      ids: ids,
      mode: spanTreeMode.mode,
      isExpanded: spanTreeMode.isExpanded
    });
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();

    draggingRef.current = {
      startX: e.clientX,
      startColWidth: colWidth
    };
    
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;

    const { startColWidth, startX } = draggingRef.current;

    const delta = e.clientX - startX;

    setColWidth(clamp(startColWidth + delta, 400, 1000));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = null;

    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <div
      style={{
        display: "grid",

        gridTemplateColumns: `${colWidth}px 1fr`,

        width: "100%",

        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius}px`,

        overflow: "hidden",
      }}
    >
      <div
        style={{
          gridColumn: 1,

          display: "flex",
          justifyContent: "space-between",

          padding: `${theme.spacing(0, 1)}`,

          borderRadius: `${theme.shape.borderRadius}px 0 0 0`,
          borderBottom: `1px solid ${theme.palette.divider}`,

          backgroundColor: theme.palette.background.paper,
        }}
      >
        <span style={{ padding: `${theme.spacing(1, 1)}` }}>Name</span>
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          style={{
            width: "6px",
            margin: "auto 0",
            cursor: "col-resize",
            border: `1px solid ${theme.palette.divider}`,
            borderWidth: "0 2px",
            transform: "translateX(50%)",
          }}
        >
          &nbsp;
        </div>
      </div>
      <div
        style={{
          gridColumn: 2,

          display: "flex",
          justifyContent: "space-between",

          padding: `${theme.spacing(1, 1)}`,

          borderRadius: `0 ${theme.shape.borderRadius}px 0 0`,
          borderBottom: `1px solid ${theme.palette.divider}`,

          backgroundColor: theme.palette.background.paper,
        }}
      >
        {steppers.map((x, i) => (
          <span key={i}>{x}</span>
        ))}
      </div>
      {spanList.filter((span) => spanTreeMode.isExpanded(span, spanTreeMode.ids)).map((span, i) => {
        return (
          <CompLane
            key={i}
            span={span}
            theme={theme}
            bounds={spanBounds}
            spanTreeMode={spanTreeMode}
            onHover={onHover}
            onClick={onClick}
            onToggle={onToggle}
          ></CompLane>
        );
      })}
    </div>
  );
}

function flattenTree(spans: ISpan[]) {
  const spanList: CompLaneSpan[] = [];
  const bounds = {
    min: NaN,
    max: NaN,
  };

  const nodes: { span: ISpan, depth: number, parent: Set<string> }[] = spans.map((span) => ({ span: span, depth: 0, parent: new Set<string>() }));

  while (nodes.length > 0) {
    const node = nodes.shift()!;
    const span = node.span;

    const compLaneSpan: CompLaneSpan = {
      id: span.id,
      span: span,
      depth: node.depth,
      color: getSpanColor(span.id),
      parents: node.parent,
    };

    spanList.push(compLaneSpan);

    const children = span.children ?? [];

    nodes.unshift(...children.map((span) => ({ span: span, depth: node.depth + 1, parent: new Set([...node.parent, node.span.id]) })));

    const a = span.start.getTime();
    const b = span.start.getTime() + span.duration;

    bounds.min = isNaN(bounds.min) ? a : Math.min(bounds.min, a);
    bounds.max = isNaN(bounds.max) ? b : Math.max(bounds.max, b);
  }

  const duration = bounds.max - bounds.min;

  
  const steps = 5;
  const steppers = ["0ms"];

  var unit = (duration / 2) < 1000 ? { f: 1, s: "ms" } : { f: 1000, s: "s" };

  for (var i = 1; i < steps + 1; ++i) {
    const step = (duration / steps) * i;

    steppers.push(`${Math.floor(step / unit.f)}${unit.s}`);
  }

  return {
    spanList,
    spanBounds: {
      min: bounds.min,
      max: bounds.max,

      duration: duration,
    },
    steppers: steppers,
  };
}

export interface ISpan {
  id: string;
  name: string;
  start: Date;
  duration: number;
  children: ISpan[] | null;
}